import React, { useState } from 'react';
import { Box, Card, CardHeader, CardBody, Heading, Text, Button, Flex, Icon, Badge, useToast, Divider } from '@chakra-ui/react';
import { FiAlertCircle, FiCheck, FiFileText } from 'react-icons/fi';
import FqPaClaimDetailShell from './FqPaClaimDetailShell';

// PA story: payer incorrectly bundled a same-day dental encounter with a medical
// encounter. Unlike some states, PA pays multiple same-day encounters of
// DIFFERENT types (medical + dental + BH + vision) \u2014 so this denial is appealable.
const mockData = {
  type: '835 Denial',
  patient: { first_name: 'Jerome', last_name: 'Baker', member_id: 'KF771203944' },
  provider: { name: 'Alice Tran, DMD', npi: '1447209866' },
  claim: {
    diagnoses: ['K02.9'], // Dental caries
    procedures: [
      { cpt: 'T1015', modifiers: [], charge: 142.00 },  // FQHC dental encounter (first line)
      { cpt: 'D0120', modifiers: [], charge: 0.00 },    // Periodic oral exam (tracking line)
      { cpt: 'D1110', modifiers: [], charge: 0.00 }     // Prophylaxis (tracking line)
    ],
    clinical_note: "Periodic oral evaluation and adult prophylaxis completed. Two carious lesions noted, restorative visit scheduled. Note: patient was seen earlier today by Dr. Nguyen (medical) for hypertension follow-up \u2014 separate encounter, billed separately per PA FQHC policy."
  }
};

const FqPaDemoSameDayRestraint = () => {
  const [isDrafted, setIsDrafted] = useState(false);
  const toast = useToast();

  const handleDraftAppeal = () => {
    setIsDrafted(true);
    toast({
      title: "Appeal Drafted",
      description: "Appeal citing the PA FQHC/RHC same-day multi-encounter policy has been generated and attached to the claim.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const rightPanel = (
    <Card borderColor="green.200" borderWidth="2px" shadow="md">
      <CardHeader bg="green.50" pb={4}>
        <Flex justify="space-between" align="center">
          <Heading size="md" display="flex" alignItems="center" color="green.800">
            <Icon as={FiAlertCircle} mr={2} />
            Revedy Analysis
          </Heading>
          <Badge colorScheme="green" variant="solid">Tier B (Appeal)</Badge>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box mb={4}>
          <Text fontWeight="bold" color="red.600" mb={1}>Denial Reason (CARC 97):</Text>
          <Text fontSize="sm">The benefit for this service is included in the payment/allowance for another service/procedure that has already been adjudicated.</Text>
        </Box>

        <Divider my={4} />

        <Box mb={4}>
          <Text fontWeight="bold" color="green.700" mb={1}>Revedy Recommendation: APPEAL \u2014 payer applied the wrong rule</Text>
          <Text fontSize="sm" mb={2}>
            The MCO bundled this dental encounter into the same-day medical encounter. Pennsylvania is explicit that FQHCs may bill more than one encounter for the same patient on the same day when the encounters are of different types \u2014 medical, dental, behavioral health, and vision each generate a separately payable PPS encounter.
          </Text>
          <Text fontSize="sm">
            In some states this denial would be correct; in PA it is not. Estimated recovery: <b>$142.00</b> (dental PPS encounter).
          </Text>
        </Box>

        <Box mb={4} p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="green.400">
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Rule Citations:</Text>
          <Text fontSize="sm" fontStyle="italic" mb={2}>"FQHCs/RHCs may bill for more than one encounter (such as a medical encounter, and a dental and/or a behavioral health encounter) for the same patient on the same day." (DHS FQHC/RHC FAQ \u2014 HealthChoices)</Text>
          <Text fontSize="sm" fontStyle="italic">OMAP MA Bulletin (Mar. 1, 2024) \u2014 vision encounters payable in addition to physical health, behavioral health and dental encounters on the same day.</Text>
        </Box>

        <Box mb={6}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Note (what would NOT be appealable):</Text>
          <Text fontSize="sm" fontWeight="medium">Two encounters of the SAME type (e.g., two medical T1015s) on one date are not separately payable without documentation of a distinct later-day illness or injury \u2014 those are handled in the Pre-Submission Firewall (Box 80 justification), not appealed.</Text>
        </Box>

        <Flex gap={3}>
          <Button
            flex="1"
            colorScheme="green"
            leftIcon={isDrafted ? <FiCheck /> : <FiFileText />}
            onClick={handleDraftAppeal}
            isDisabled={isDrafted}
          >
            {isDrafted ? 'Appeal Drafted' : 'Generate Appeal Letter'}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );

  return <FqPaClaimDetailShell claimData={mockData} rightPanel={rightPanel} backTo="/fq-pa/835-review" />;
};

export default FqPaDemoSameDayRestraint;
